import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AssessmentId,
  getCoachConfig,
  listCoachSessions,
} from "../../services/rest/kairaAssessments";
import { flattenCoachAnswers } from "../../organisms/PatientDetailsV2/CoachAssessment/useCoachAssessment";
import {
  pickPreferredSession,
  sessionCount,
  sessionKey,
} from "../../organisms/PatientDetailsV2/CoachAssessment/prevHistory";
import {
  isSharedProfileSection,
  questionIsVisible,
} from "../../organisms/PatientDetailsV2/SharedProfile/showIf";

export type QnASection = {
  id: string;
  name: string;
  questions: any[];
  /** Likert scales of the config this section came from. */
  scales?: any;
};

export type SessionDetail = {
  answers: Record<string, unknown>;
  sections: QnASection[];
  scales: any;
};

/** Config sections (minus Shared Profile) with the questions visible for these answers. */
const buildDetail = (config: any, rawAnswers: unknown): SessionDetail => {
  const answers = flattenCoachAnswers(rawAnswers as any, config);
  const questions = config?.questions || [];
  const sections = (config?.sections || [])
    .filter((section: any) => !isSharedProfileSection(section))
    .map((section: any) => ({
      id: String(section.id),
      name: section.name || section.title || "",
      questions: questions.filter(
        (q: any) =>
          q.sectionId === section.id && questionIsVisible(q, answers),
      ),
    }))
    .filter((section: QnASection) => section.questions.length > 0);
  return { answers, sections, scales: config?.scales };
};

/** Read-only baseline + follow-up sessions for one assessment type. */
export const useAssessmentQnA = (
  assessmentId: AssessmentId,
  patientId: string | undefined,
) => {
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Keyed by session id so a slow or failed visit never leaks into another.
  const [details, setDetails] = useState<Record<string, SessionDetail>>({});
  const [detailLoading, setDetailLoading] = useState<Record<string, boolean>>({});
  const [detailError, setDetailError] = useState<Record<string, string>>({});
  const requestedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    requestedRef.current = new Set();
    setDetails({});
    setDetailLoading({});
    setDetailError({});
    if (!patientId) {
      setSessions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    listCoachSessions(patientId, assessmentId)
      .then((data) => {
        if (!cancelled) setSessions(data?.sessions || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load assessments");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assessmentId, patientId]);

  // Same pick as the editor (useCoachAssessment) in Baseline mode.
  const baseline = useMemo(
    () => pickPreferredSession(sessions.filter((s) => sessionCount(s) === 0)),
    [sessions],
  );

  const followUps = useMemo(
    () =>
      sessions
        .filter((s) => (sessionCount(s) ?? 0) >= 1)
        .sort((a, b) => (sessionCount(b) || 0) - (sessionCount(a) || 0)),
    [sessions],
  );

  /** Fetch the config that matches this visit (baseline and follow-up seeds differ). */
  const loadDetail = useCallback(
    async (session: any) => {
      const id = sessionKey(session);
      if (!patientId || !id || requestedRef.current.has(id)) return;
      const requested = requestedRef.current;
      requested.add(id);
      setDetailLoading((prev) => ({ ...prev, [id]: true }));
      setDetailError(({ [id]: _cleared, ...rest }) => rest);
      try {
        const config = await getCoachConfig(assessmentId, patientId, {
          sessionId: id,
          assessmentCount: sessionCount(session),
        });
        if (requested !== requestedRef.current) return;
        setDetails((prev) => ({
          ...prev,
          [id]: buildDetail(config, session.answers),
        }));
      } catch (err: any) {
        if (requested !== requestedRef.current) return;
        requested.delete(id);
        setDetailError((prev) => ({
          ...prev,
          [id]: err?.message || "Failed to load assessment",
        }));
      } finally {
        if (requested === requestedRef.current) {
          setDetailLoading((prev) => ({ ...prev, [id]: false }));
        }
      }
    },
    [assessmentId, patientId],
  );

  return {
    loading,
    error,
    baseline,
    followUps,
    details,
    detailLoading,
    detailError,
    loadDetail,
    sessionKey,
  };
};
